import { Category, LanguageIsoCode } from '@/graphql/graphql';
import { Link } from '@/i18n/navigation';
import { CategoryDropdownMenu } from '@/types/categories';
import { ChevronDown } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type Props = {
    isActive?: boolean;
    categories: CategoryDropdownMenu[];
    onLinkClickAction?: () => void;
};

export default function CategoriesDropDown({ isActive, categories, onLinkClickAction }: Props) {
    const locale = useLocale().toLowerCase();
    const t = useTranslations('Navigation');
    const router = useRouter();
    const searchParams = useSearchParams();

    const [catMenuOpen, setCatMenuOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const listItemClassName: string = `flex rounded-[8px] p-[10px] my-2 relative text-base
                                        hover:bg-[rgba(255,255,255,0.30)] cursor-pointer
                                        after:content-[''] after:absolute
                                        after:-bottom-1 after:left-0
                                        after:w-full after:h-[1px]
                                        after:bg-[rgba(255,255,255,0.10)]`;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                (event.target instanceof Element && event.target.matches('a')) ||
                (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target))
            ) {
                setCatMenuOpen(false);
            }
        };

        if (catMenuOpen) document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [catMenuOpen]);

    const handleSearch = (searchTerm?: string, isGenre?: boolean) => {
        if (onLinkClickAction) {
            onLinkClickAction();
        }

        if (searchTerm) {
            const term = searchTerm.trim();
            const params = new URLSearchParams(searchParams.toString());
            params.set('category', term);

            if (onLinkClickAction) {
                onLinkClickAction();
            }

            router.push(`/programs?${isGenre ? 'gens' : 'category'}=${encodeURIComponent(term)}`);
        } else {
            router.push('/programs');
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className={`
                        px-3 py-3 rounded-[8px] cursor-pointer flex
                        transition-colors text-white hover:text-gray-400 duration-300
                        ${isActive ? 'sm:bg-[rgba(255,255,255,0.30)]' : 'bg-[transparent]'}
                    `}
                onClick={() => setCatMenuOpen(!catMenuOpen)}
            >
                <span className="pr-[10px]">{t('programs')}</span>
                <ChevronDown />
            </button>
            <ul
                className={`
                    px-[10px] pt-1 pb-[0.5px] absolute bg-[rgba(14,14,14,1)] rounded-[8px] z-1
                    w-[calc(100vw-32px)] sm:w-[205px]
                    md:w-auto
                    md:max-w-[300px]
                    lg:max-w-[400px]
                    max-h-[440px] sm:max-h-[initial]
                    overflow-y-auto
                    ${catMenuOpen ? 'block' : 'hidden'}
                `}
            >
                <li>
                    <Link
                        className={listItemClassName}
                        href="/programs"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSearch();
                        }}
                    >
                        {t('allPrograms')}
                    </Link>
                </li>
                {categories.map((category, index, array) => (
                    <li key={category.identifier.id}>
                        <Link
                            className={`whitespace-nowrap  ${listItemClassName} ${index === array.length - 1 ? 'last:after:content-[none]' : ''}`}
                            href={{
                                pathname: '/programs',
                                query: { category: category.identifier.id },
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleSearch(category.identifier.id, category.isAnExcludedGenre);
                            }}
                        >
                            {category.titles[locale as LanguageIsoCode] as string}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
